<script setup>
import {computed, ref, watch} from 'vue';

const props = defineProps({
    content: { type: Object, default: () => ({}) },
});

const min = computed(() => Number(props.content?.quantity_min ?? 1));
const max = computed(() => Number(props.content?.quantity_max ?? 25));
const step = computed(() => Number(props.content?.quantity_step ?? 1));
const currentQuantity = ref(min.value);

watch([min, max], ([nextMin, nextMax]) => {
    currentQuantity.value = Math.min(Math.max(currentQuantity.value, nextMin), nextMax);
}, { immediate: true });

const heading = computed(() => props.content?.heading || 'Estimate your monthly cost');
const description = computed(() => props.content?.description || 'Adjust the quantity to see the total update instantly.');
const quantityLabel = computed(() => props.content?.quantity_label || 'Seats');
const unitLabel = computed(() => props.content?.unit_label || 'per seat');
const basePrice = computed(() => Number(props.content?.base_price ?? 120));
const currencySymbol = computed(() => props.content?.currency_symbol || '$');
const summaryLabel = computed(() => props.content?.summary_label || 'Estimated total');
const note = computed(() => props.content?.note || '');

const totalPrice = computed(() => {
    return currentQuantity.value * basePrice.value;
});

const formatPrice = (value) => {
    if (!Number.isFinite(value)) {
        return `${currencySymbol.value}0`;
    }

    const hasDecimals = Math.round(value * 100) % 100 !== 0;
    return `${currencySymbol.value}${value.toLocaleString(undefined, {
        minimumFractionDigits: hasDecimals ? 2 : 0,
        maximumFractionDigits: hasDecimals ? 2 : 0,
    })}`;
};
</script>

<template>
    <div class="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <div class="mb-6">
            <p class="text-xs font-semibold uppercase tracking-[0.18em] text-sky-500">{{ quantityLabel }}</p>
            <h3 class="mt-2 text-2xl font-semibold text-slate-900">{{ heading }}</h3>
            <p class="mt-2 text-sm leading-6 text-slate-500">{{ description }}</p>
        </div>

        <div class="space-y-5">
            <div class="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div class="flex items-end justify-between gap-4">
                    <div>
                        <p class="text-sm font-medium text-slate-600">{{ quantityLabel }}</p>
                        <p class="mt-1 text-3xl font-semibold text-slate-900">{{ currentQuantity }}</p>
                    </div>
                    <div class="text-right">
                        <p class="text-sm font-medium text-slate-600">{{ unitLabel }}</p>
                        <p class="mt-1 text-xl font-semibold text-slate-900">{{ formatPrice(basePrice) }}</p>
                    </div>
                </div>

                <input
                    v-model.number="currentQuantity"
                    class="mt-4 h-2 w-full cursor-pointer appearance-none rounded-full bg-slate-200 accent-sky-500"
                    type="range"
                    :min="min"
                    :max="max"
                    :step="step"
                >
            </div>

            <div class="grid gap-4 md:grid-cols-[minmax(0,1fr)_auto] md:items-end">
                <label class="block">
                    <span class="text-sm font-medium text-slate-600">{{ quantityLabel }}</span>
                    <input
                        v-model.number="currentQuantity"
                        class="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 text-base text-slate-900 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-100"
                        type="number"
                        :min="min"
                        :max="max"
                        :step="step"
                    >
                </label>

                <div class="rounded-2xl bg-slate-900 px-5 py-4 text-white">
                    <p class="text-xs uppercase tracking-[0.18em] text-slate-300">{{ summaryLabel }}</p>
                    <p class="mt-2 text-3xl font-semibold">{{ formatPrice(totalPrice) }}</p>
                </div>
            </div>

            <p v-if="note" class="text-sm leading-6 text-slate-500">{{ note }}</p>
        </div>
    </div>
</template>
